import React, { useEffect, useMemo, useRef, useState } from "react"
import { createConsumer } from "@rails/actioncable"
import { createRoot } from "react-dom/client"
import { Group, Layer, Line, Rect, Stage, Text } from "react-konva"

const BOARD_WIDTH = 1100
const BOARD_HEIGHT = 700
const DRAW_COLOR = "#244133"
const BACKGROUND_NAME = "whiteboard-background"
const TILE_SIZE = 58
const LETTERS = "abcdefghijklmnopqrstuvwxyz".split("")
const BLANK_TILE = ""
const TRAY_TILES = [...LETTERS, BLANK_TILE]
const VOWELS = new Set(["a", "e", "i", "o", "u"])
const DEFAULT_BINGO_GRID = {
  enabled: false,
  rows: 5,
  columns: 5,
  cells: Array(25).fill(""),
  updated_at: 0,
  cell_updated_ats: Array(25).fill(0)
}
const DEFAULT_CARD_DECK = {
  enabled: false,
  mode: "grid",
  draw_index: 0,
  cards: [],
  updated_at: 0
}
const DEFAULT_TOOL_UPDATED_ATS = {
  letter_tile_mat: 0,
  bingo_grid: 0,
  card_deck: 0
}
const DEFAULT_DELETED_OBJECTS = {}
const DEFAULT_STUDENT_MODE = "draw"
const MODE_LABELS = {
  draw: "Draw",
  type: "Type",
  drag: "Select",
  erase: "Erase"
}
const STUDENT_MODE_ICONS = {
  draw: "✏️",
  type: "⌨️",
  drag: "↔"
}

const STUDENT_MODES = ["draw", "type", "drag", "erase"]
const STUDENT_PRIMARY_MODES = ["draw", "type", "drag"]

const StudentEraserIcon = () => (
  React.createElement(
    "svg",
    {
      "aria-hidden": "true",
      className: "student-eraser-icon",
      viewBox: "0 0 36 28"
    },
    [
      React.createElement("polygon", {
        className: "student-eraser-icon__shadow",
        key: "shadow",
        points: "7,21 23,21 30,25 14,25"
      }),
      React.createElement("polygon", {
        className: "student-eraser-icon__top",
        key: "top",
        points: "12,5 30,5 24,11 6,11"
      }),
      React.createElement("polygon", {
        className: "student-eraser-icon__front",
        key: "front",
        points: "6,11 24,11 19,21 1,21"
      }),
      React.createElement("polygon", {
        className: "student-eraser-icon__side",
        key: "side",
        points: "24,11 30,5 25,15 19,21"
      }),
      React.createElement("polygon", {
        className: "student-eraser-icon__sleeve-top",
        key: "sleeve-top",
        points: "21,5 30,5 24,11 15,11"
      }),
      React.createElement("polygon", {
        className: "student-eraser-icon__sleeve-front",
        key: "sleeve-front",
        points: "15,11 24,11 19,21 10,21"
      }),
      React.createElement("line", {
        className: "student-eraser-icon__highlight",
        key: "highlight",
        x1: "10",
        x2: "19",
        y1: "8",
        y2: "8"
      }),
      React.createElement("line", {
        className: "student-eraser-icon__edge",
        key: "edge-top",
        x1: "24",
        x2: "19",
        y1: "11",
        y2: "21"
      }),
      React.createElement("line", {
        className: "student-eraser-icon__edge",
        key: "edge-left",
        x1: "6",
        x2: "1",
        y1: "11",
        y2: "21"
      })
    ]
  )
)

const buildClientId = () => `client-${Math.random().toString(36).slice(2, 10)}`
const currentTimestamp = () => Date.now()
const normalizeTimestamp = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}
const normalizeDeletedObjects = (rawDeletedObjects) => Object.fromEntries(
  Object.entries(rawDeletedObjects || {}).map(([id, timestamp]) => [id, normalizeTimestamp(timestamp)])
)
const normalizeStudentMode = (rawMode) => (STUDENT_MODES.includes(rawMode) ? rawMode : DEFAULT_STUDENT_MODE)
const normalizeToolUpdatedAts = (rawToolUpdatedAts) => ({
  ...DEFAULT_TOOL_UPDATED_ATS,
  ...Object.fromEntries(
    Object.entries(rawToolUpdatedAts || {}).map(([toolName, timestamp]) => [toolName, normalizeTimestamp(timestamp)])
  )
})

const normalizeBingoGrid = (rawGrid) => {
  const rows = Math.max(1, Math.min(Number(rawGrid?.rows) || DEFAULT_BINGO_GRID.rows, 6))
  const columns = Math.max(1, Math.min(Number(rawGrid?.columns) || DEFAULT_BINGO_GRID.columns, 6))
  const cellCount = rows * columns
  const rawCells = Array.isArray(rawGrid?.cells) ? rawGrid.cells : []

  return {
    enabled: Boolean(rawGrid?.enabled),
    rows,
    columns,
    cells: Array.from({ length: cellCount }, (_, index) => String(rawCells[index] || "")),
    updated_at: normalizeTimestamp(rawGrid?.updated_at),
    cell_updated_ats: Array.from({ length: cellCount }, (_, index) => normalizeTimestamp(rawGrid?.cell_updated_ats?.[index]))
  }
}

const buildBingoGridLayout = (grid) => {
  const maxWidth = 640
  const maxHeight = 500
  const gap = 10
  const cellWidth = Math.min(120, (maxWidth - gap * (grid.columns - 1)) / grid.columns)
  const cellHeight = Math.min(96, (maxHeight - gap * (grid.rows - 1)) / grid.rows)
  const width = cellWidth * grid.columns + gap * (grid.columns - 1)
  const height = cellHeight * grid.rows + gap * (grid.rows - 1)

  return {
    x: (BOARD_WIDTH - width) / 2,
    y: (BOARD_HEIGHT - height) / 2,
    width,
    height,
    gap,
    cellWidth,
    cellHeight
  }
}

const buildCardDeckLayout = (cardCount) => {
  const columns = Math.max(2, Math.min(4, Math.ceil(Math.sqrt(Math.max(cardCount, 1)))))
  const rows = Math.max(1, Math.ceil(cardCount / columns))
  const gap = 16
  const cardWidth = 118
  const cardHeight = 156
  const width = columns * cardWidth + (columns - 1) * gap
  const height = rows * cardHeight + (rows - 1) * gap

  return {
    columns,
    rows,
    gap,
    cardWidth,
    cardHeight,
    width,
    height,
    x: (BOARD_WIDTH - width) / 2,
    y: (BOARD_HEIGHT - height) / 2
  }
}

const cardDeckTextFromCards = (cards) => cards.map((card) => card.label).join("\n")

const layoutCardDeckCards = (cards) => {
  const layout = buildCardDeckLayout(cards.length)

  return cards.map((card, index) => {
    const row = Math.floor(index / layout.columns)
    const column = index % layout.columns

    return {
      ...card,
      x: layout.x + column * (layout.cardWidth + layout.gap),
      y: layout.y + row * (layout.cardHeight + layout.gap)
    }
  })
}

const buildCardPileLayout = () => ({
  pileX: 40,
  pileY: 150,
  revealX: 220,
  revealY: 130,
  revealColumns: 2,
  revealGap: 18,
  cardWidth: 118,
  cardHeight: 156
})

const layoutDrawnPileCards = (cards, pileLayout, drawIndex) => (
  cards.map((card, index) => {
    if (index >= drawIndex) {
      return {
        ...card,
        x: pileLayout.pileX,
        y: pileLayout.pileY
      }
    }

    const revealIndex = index
    const row = Math.floor(revealIndex / pileLayout.revealColumns)
    const column = revealIndex % pileLayout.revealColumns
    const fallbackX = pileLayout.revealX + column * (pileLayout.cardWidth + pileLayout.revealGap)
    const fallbackY = pileLayout.revealY + row * (pileLayout.cardHeight + pileLayout.revealGap)

    return {
      ...card,
      x: Number.isFinite(card.x) ? card.x : fallbackX,
      y: Number.isFinite(card.y) ? card.y : fallbackY
    }
  })
)

const normalizeCardDeck = (rawDeck) => {
  const pileLayout = buildCardPileLayout()
  const rawCards = Array.isArray(rawDeck?.cards) ? rawDeck.cards : []
  const normalizedCards = rawCards
    .map((card, index) => ({
      id: String(card?.id || `card-${index + 1}`),
      label: String(card?.label || ""),
      faceUp: Boolean(card?.faceUp),
      x: Number(card?.x),
      y: Number(card?.y),
      updated_at: normalizeTimestamp(card?.updated_at)
    }))
    .filter((card) => card.label.length > 0)
  const mode = rawDeck?.mode === "pile" ? "pile" : "grid"
  const gridCards = layoutCardDeckCards(normalizedCards).map((card, index) => ({
    ...card,
    x: Number.isFinite(normalizedCards[index]?.x) ? normalizedCards[index].x : card.x,
    y: Number.isFinite(normalizedCards[index]?.y) ? normalizedCards[index].y : card.y
  }))
  const drawIndex = Math.max(0, Math.min(Number(rawDeck?.draw_index) || 0, gridCards.length))
  const positionedCards = mode === "pile" ? layoutDrawnPileCards(gridCards, pileLayout, drawIndex) : gridCards

  return {
    enabled: Boolean(rawDeck?.enabled),
    mode,
    draw_index: drawIndex,
    updated_at: normalizeTimestamp(rawDeck?.updated_at),
    cards: positionedCards
  }
}

const normalizeBoardObject = (object) => ({
  ...object,
  updated_at: normalizeTimestamp(object?.updated_at)
})

const boardObjectRenderPriority = (object) => {
  if (object.type === "tile") return 2
  if (object.type === "text") return 1
  return 0
}

const mergeDeletedObjects = (currentDeletedObjects, incomingDeletedObjects) => {
  const merged = { ...currentDeletedObjects }

  Object.entries(incomingDeletedObjects).forEach(([id, timestamp]) => {
    merged[id] = Math.max(merged[id] || 0, normalizeTimestamp(timestamp))
  })

  return merged
}

const mergeObjects = (currentObjects, incomingObjects, deletedObjects) => {
  const currentById = new Map(currentObjects.map((object) => [object.id, normalizeBoardObject(object)]))
  const incomingById = new Map(incomingObjects.map((object) => [object.id, normalizeBoardObject(object)]))
  const orderedIds = [...currentObjects.map((object) => object.id), ...incomingObjects.map((object) => object.id)]

  return [...new Set(orderedIds)].flatMap((id) => {
    const currentObject = currentById.get(id)
    const incomingObject = incomingById.get(id)
    const mergedObject = [currentObject, incomingObject]
      .filter(Boolean)
      .sort((left, right) => right.updated_at - left.updated_at)[0]

    if (!mergedObject) return []
    if ((deletedObjects[id] || 0) > mergedObject.updated_at) return []

    return [mergedObject]
  })
}

const mergeBingoGrid = (currentGrid, incomingGrid) => {
  if (currentGrid.rows !== incomingGrid.rows || currentGrid.columns !== incomingGrid.columns) {
    return incomingGrid.updated_at >= currentGrid.updated_at ? incomingGrid : currentGrid
  }

  const cellCount = currentGrid.rows * currentGrid.columns

  return {
    enabled: incomingGrid.updated_at >= currentGrid.updated_at ? incomingGrid.enabled : currentGrid.enabled,
    rows: currentGrid.rows,
    columns: currentGrid.columns,
    updated_at: Math.max(currentGrid.updated_at, incomingGrid.updated_at),
    cells: Array.from({ length: cellCount }, (_, index) => (
      incomingGrid.cell_updated_ats[index] >= currentGrid.cell_updated_ats[index] ? incomingGrid.cells[index] : currentGrid.cells[index]
    )),
    cell_updated_ats: Array.from({ length: cellCount }, (_, index) => (
      Math.max(currentGrid.cell_updated_ats[index], incomingGrid.cell_updated_ats[index])
    ))
  }
}

const mergeCardDeck = (currentDeck, incomingDeck) => {
  const currentIds = currentDeck.cards.map((card) => card.id)
  const incomingIds = incomingDeck.cards.map((card) => card.id)

  if (
    currentDeck.mode !== incomingDeck.mode ||
    currentDeck.enabled !== incomingDeck.enabled ||
    currentIds.join("|") !== incomingIds.join("|")
  ) {
    return incomingDeck.updated_at >= currentDeck.updated_at ? incomingDeck : currentDeck
  }

  const currentCards = new Map(currentDeck.cards.map((card) => [card.id, card]))
  const incomingCards = new Map(incomingDeck.cards.map((card) => [card.id, card]))

  return {
    enabled: currentDeck.enabled,
    mode: currentDeck.mode,
    draw_index: Math.max(currentDeck.draw_index, incomingDeck.draw_index),
    updated_at: Math.max(currentDeck.updated_at, incomingDeck.updated_at),
    cards: currentIds.map((cardId) => {
      const currentCard = currentCards.get(cardId)
      const incomingCard = incomingCards.get(cardId)
      return [currentCard, incomingCard].sort((left, right) => right.updated_at - left.updated_at)[0]
    })
  }
}

const mergeBoardStates = (currentState, incomingState) => {
  const normalizedCurrentState = parseBoardState(JSON.stringify(currentState))
  const normalizedIncomingState = parseBoardState(JSON.stringify(incomingState))
  const deletedObjects = mergeDeletedObjects(
    normalizedCurrentState.deleted_objects,
    normalizedIncomingState.deleted_objects
  )
  const toolUpdatedAts = {
    letter_tile_mat: Math.max(
      normalizedCurrentState.tool_updated_ats.letter_tile_mat,
      normalizedIncomingState.tool_updated_ats.letter_tile_mat
    ),
    bingo_grid: Math.max(
      normalizedCurrentState.tool_updated_ats.bingo_grid,
      normalizedIncomingState.tool_updated_ats.bingo_grid
    ),
    card_deck: Math.max(
      normalizedCurrentState.tool_updated_ats.card_deck,
      normalizedIncomingState.tool_updated_ats.card_deck
    )
  }

  return {
    objects: mergeObjects(normalizedCurrentState.objects, normalizedIncomingState.objects, deletedObjects),
    tools: {
      letter_tile_mat:
        normalizedIncomingState.tool_updated_ats.letter_tile_mat >= normalizedCurrentState.tool_updated_ats.letter_tile_mat
          ? normalizedIncomingState.tools.letter_tile_mat
          : normalizedCurrentState.tools.letter_tile_mat,
      bingo_grid: mergeBingoGrid(
        normalizedCurrentState.tools.bingo_grid,
        normalizedIncomingState.tools.bingo_grid
      ),
      card_deck: mergeCardDeck(
        normalizedCurrentState.tools.card_deck,
        normalizedIncomingState.tools.card_deck
      )
    },
    student_mode:
      normalizedIncomingState.student_mode_updated_at >= normalizedCurrentState.student_mode_updated_at
        ? normalizedIncomingState.student_mode
        : normalizedCurrentState.student_mode,
    student_mode_updated_at: Math.max(
      normalizedCurrentState.student_mode_updated_at,
      normalizedIncomingState.student_mode_updated_at
    ),
    tool_updated_ats: toolUpdatedAts,
    deleted_objects: deletedObjects,
    updated_at: Math.max(normalizedCurrentState.updated_at, normalizedIncomingState.updated_at)
  }
}

const parseBoardState = (rawState) => {
  try {
    const parsed = JSON.parse(rawState)
    if (!Array.isArray(parsed.objects)) {
      return {
        objects: [],
        tools: { letter_tile_mat: false, bingo_grid: { ...DEFAULT_BINGO_GRID }, card_deck: { ...DEFAULT_CARD_DECK } },
        tool_updated_ats: { ...DEFAULT_TOOL_UPDATED_ATS },
        student_mode: DEFAULT_STUDENT_MODE,
        student_mode_updated_at: 0,
        deleted_objects: { ...DEFAULT_DELETED_OBJECTS },
        updated_at: currentTimestamp()
      }
    }

    return {
      objects: parsed.objects.map(normalizeBoardObject),
      tools: {
        letter_tile_mat: Boolean(parsed.tools?.letter_tile_mat),
        bingo_grid: normalizeBingoGrid(parsed.tools?.bingo_grid),
        card_deck: normalizeCardDeck(parsed.tools?.card_deck)
      },
      tool_updated_ats: normalizeToolUpdatedAts(parsed.tool_updated_ats),
      student_mode: normalizeStudentMode(parsed.student_mode),
      student_mode_updated_at: normalizeTimestamp(parsed.student_mode_updated_at),
      deleted_objects: normalizeDeletedObjects(parsed.deleted_objects),
      updated_at: normalizeTimestamp(parsed.updated_at) || currentTimestamp()
    }
  } catch {
    return {
      objects: [],
      tools: { letter_tile_mat: false, bingo_grid: { ...DEFAULT_BINGO_GRID }, card_deck: { ...DEFAULT_CARD_DECK } },
      tool_updated_ats: { ...DEFAULT_TOOL_UPDATED_ATS },
      student_mode: DEFAULT_STUDENT_MODE,
      student_mode_updated_at: 0,
      deleted_objects: { ...DEFAULT_DELETED_OBJECTS },
      updated_at: currentTimestamp()
    }
  }
}

const normalizeSelectionBox = (selectionBox) => {
  const x = Math.min(selectionBox.startX, selectionBox.endX)
  const y = Math.min(selectionBox.startY, selectionBox.endY)
  const width = Math.abs(selectionBox.endX - selectionBox.startX)
  const height = Math.abs(selectionBox.endY - selectionBox.startY)

  return { x, y, width, height }
}

const intersects = (a, b) =>
  a.x < b.x + b.width &&
  a.x + a.width > b.x &&
  a.y < b.y + b.height &&
  a.y + a.height > b.y

const objectBounds = (object) => {
  if (object.type === "text") {
    const width = Math.max(object.text.length, 1) * object.fontSize * 0.6
    const height = object.fontSize * 1.3

    return {
      x: object.x,
      y: object.y,
      width,
      height
    }
  }

  if (object.type === "tile") {
    return {
      x: object.x,
      y: object.y,
      width: TILE_SIZE,
      height: TILE_SIZE
    }
  }

  const xOffset = object.x || 0
  const yOffset = object.y || 0
  const xs = []
  const ys = []

  for (let index = 0; index < object.points.length; index += 2) {
    xs.push(object.points[index] + xOffset)
    ys.push(object.points[index + 1] + yOffset)
  }

  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)

  return {
    x: minX - 6,
    y: minY - 6,
    width: Math.max(maxX - minX, 1) + 12,
    height: Math.max(maxY - minY, 1) + 12
  }
}

const sessionConsumer = createConsumer()

function LiveSessionApp({ initialState, role, sessionId, updateUrl }) {
  const isTeacher = role === "teacher"
  const [mode, setMode] = useState(role === "student" ? normalizeStudentMode(initialState.student_mode) : "draw")
  const [boardState, setBoardState] = useState(initialState)
  const [selectedIds, setSelectedIds] = useState([])
  const [selectionBox, setSelectionBox] = useState(null)
  const [selectionPreviewIds, setSelectionPreviewIds] = useState([])
  const [textDraft, setTextDraft] = useState(null)
  const [gridCellDraft, setGridCellDraft] = useState(null)
  const [cardDeckDraft, setCardDeckDraft] = useState(cardDeckTextFromCards(initialState.tools?.card_deck?.cards || []))
  const [showBingoSetup, setShowBingoSetup] = useState(false)
  const [showCardSetup, setShowCardSetup] = useState(Boolean(initialState.tools?.card_deck?.enabled))
  const [showStudentControls, setShowStudentControls] = useState(false)
  const [showClearMenu, setShowClearMenu] = useState(false)
  const [cardShuffleFeedback, setCardShuffleFeedback] = useState(false)
  const [stageScale, setStageScale] = useState(1)
  const stageContainerRef = useRef(null)
  const currentLineRef = useRef(null)
  const syncTimerRef = useRef(null)
  const clientIdRef = useRef(buildClientId())
  const textInputRef = useRef(null)
  const shouldSyncRef = useRef(false)
  const dragGroupRef = useRef(null)
  const textDraftFocusKeyRef = useRef(null)
  const trayDragRef = useRef(null)
  const cardPointerRef = useRef(null)
  const pilePointerRef = useRef(null)
  const cardShuffleFeedbackTimerRef = useRef(null)
  const scale = useMemo(() => stageScale, [stageScale])
  const canvasWidth = BOARD_WIDTH * scale
  const canvasHeight = BOARD_HEIGHT * scale
  const letterTileMatEnabled = Boolean(boardState.tools?.letter_tile_mat)
  const bingoGrid = normalizeBingoGrid(boardState.tools?.bingo_grid)
  const bingoGridEnabled = bingoGrid.enabled
  const bingoGridLayout = buildBingoGridLayout(bingoGrid)
  const cardDeck = normalizeCardDeck(boardState.tools?.card_deck)
  const cardDeckEnabled = cardDeck.enabled
  const cardDeckLayout = buildCardDeckLayout(cardDeck.cards.length)
  const cardPileLayout = buildCardPileLayout()
  const cardDeckIsPile = cardDeck.mode === "pile"
  const remainingPileCount = Math.max(cardDeck.cards.length - cardDeck.draw_index, 0)
  const drawnPileCards = cardDeckIsPile ? cardDeck.cards.slice(0, cardDeck.draw_index) : []
  const activeSelectedIds = selectionPreviewIds.length > 0 ? selectionPreviewIds : selectedIds
  const renderedBoardObjects = useMemo(
    () => [...boardState.objects].sort((left, right) => boardObjectRenderPriority(left) - boardObjectRenderPriority(right)),
    [boardState.objects]
  )

  useEffect(() => {
    const refreshSize = () => {
      const wrap = stageContainerRef.current
      if (!wrap) return

      const horizontalPadding = 48
      const verticalPadding = 48
      const availableWidth = Math.max((wrap.clientWidth || BOARD_WIDTH) - horizontalPadding, 320)

      if (role === "student") {
        const availableHeight = Math.max((wrap.clientHeight || BOARD_HEIGHT) - verticalPadding, 240)
        setStageScale(Math.min(availableWidth / BOARD_WIDTH, availableHeight / BOARD_HEIGHT, 1))
        return
      }

      setStageScale(Math.min(availableWidth / BOARD_WIDTH, 1))
    }

    refreshSize()
    window.addEventListener("resize", refreshSize)

    return () => window.removeEventListener("resize", refreshSize)
  }, [role, letterTileMatEnabled])

  useEffect(() => {
    const activeDraft = textDraft || gridCellDraft
    if (!activeDraft || !textInputRef.current) return

    const focusKey = `${activeDraft.kind}:${activeDraft.id || activeDraft.index || "new"}:${activeDraft.x}:${activeDraft.y}`
    if (textDraftFocusKeyRef.current === focusKey) return

    textDraftFocusKeyRef.current = focusKey

    requestAnimationFrame(() => {
      if (!textInputRef.current) return

      textInputRef.current.focus()
      textInputRef.current.select()
    })
  }, [gridCellDraft, textDraft])

  useEffect(() => {
    const subscription = sessionConsumer.subscriptions.create(
      { channel: "SessionChannel", slug: sessionId },
      {
        received: (payload) => {
          if (payload.type !== "board_state") return
          if (payload.client_id === clientIdRef.current) return

          shouldSyncRef.current = false
          setBoardState((current) => mergeBoardStates(current, payload.board_state))
          setSelectedIds([])
          setSelectionPreviewIds([])
          setSelectionBox(null)
          setTextDraft(null)
          setGridCellDraft(null)
          setCardDeckDraft(cardDeckTextFromCards(normalizeCardDeck(payload.board_state.tools?.card_deck).cards))
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [sessionId])

  useEffect(() => {
    if (bingoGridEnabled) setShowBingoSetup(true)
  }, [bingoGridEnabled])

  useEffect(() => {
    if (cardDeckEnabled) setShowCardSetup(true)
  }, [cardDeckEnabled])

  useEffect(() => {
    if (role !== "student") return

    const sharedStudentMode = normalizeStudentMode(boardState.student_mode)
    if (sharedStudentMode !== mode) {
      setMode(sharedStudentMode)
    }
  }, [boardState.student_mode, mode, role])

  useEffect(() => () => {
    if (cardShuffleFeedbackTimerRef.current) {
      clearTimeout(cardShuffleFeedbackTimerRef.current)
    }
  }, [])

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!trayDragRef.current) return

      const nextPoint = clientPointToBoardPoint(event.clientX, event.clientY)
      updateObject(trayDragRef.current.tileId, nextPoint)
    }

    const handlePointerUp = () => {
      trayDragRef.current = null
    }

    window.addEventListener("pointermove", handlePointerMove)
    window.addEventListener("pointerup", handlePointerUp)

    return () => {
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerup", handlePointerUp)
    }
  }, [])

  useEffect(() => {
    if (!shouldSyncRef.current) return
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current)

    syncTimerRef.current = setTimeout(() => {
      fetch(updateUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
          Accept: "application/json"
        },
        body: JSON.stringify({
          client_id: clientIdRef.current,
          board_state: boardState
        })
      })

      shouldSyncRef.current = false
    }, 120)

    return () => clearTimeout(syncTimerRef.current)
  }, [boardState, updateUrl])

  const clientPointToBoardPoint = (clientX, clientY) => {
    const bounds = stageContainerRef.current?.getBoundingClientRect()
    if (!bounds) {
      return { x: 120, y: BOARD_HEIGHT - 150 }
    }

    const rawX = (clientX - bounds.left - 24) / scale - TILE_SIZE / 2
    const rawY = (clientY - bounds.top - 24) / scale - TILE_SIZE / 2

    return {
      x: Math.max(0, Math.min(rawX, BOARD_WIDTH - TILE_SIZE)),
      y: Math.max(0, Math.min(rawY, BOARD_HEIGHT - TILE_SIZE))
    }
  }

  const findBingoCellIndexAtPoint = (pointX, pointY) => {
    if (!bingoGridEnabled) return null

    const relativeX = pointX - bingoGridLayout.x
    const relativeY = pointY - bingoGridLayout.y
    if (relativeX < 0 || relativeY < 0 || relativeX > bingoGridLayout.width || relativeY > bingoGridLayout.height) return null

    const strideX = bingoGridLayout.cellWidth + bingoGridLayout.gap
    const strideY = bingoGridLayout.cellHeight + bingoGridLayout.gap
    const column = Math.floor(relativeX / strideX)
    const row = Math.floor(relativeY / strideY)
    if (column < 0 || column >= bingoGrid.columns || row < 0 || row >= bingoGrid.rows) return null

    const xWithinCell = relativeX - column * strideX
    const yWithinCell = relativeY - row * strideY
    if (xWithinCell > bingoGridLayout.cellWidth || yWithinCell > bingoGridLayout.cellHeight) return null

    return row * bingoGrid.columns + column
  }

  const updateObject = (id, patch) => {
    const timestamp = currentTimestamp()
    shouldSyncRef.current = true
    setBoardState((current) => ({
      ...current,
      updated_at: timestamp,
      objects: current.objects.map((object) => (object.id === id ? { ...object, ...patch, updated_at: timestamp } : object))
    }))
  }

  const deleteObjectIds = (ids) => {
    if (ids.length === 0) return

    const timestamp = currentTimestamp()
    shouldSyncRef.current = true
    setBoardState((current) => ({
      ...current,
      updated_at: timestamp,
      deleted_objects: {
        ...current.deleted_objects,
        ...Object.fromEntries(ids.map((id) => [id, timestamp]))
      },
      objects: current.objects.filter((object) => !ids.includes(object.id))
    }))
    setSelectedIds((current) => current.filter((id) => !ids.includes(id)))
  }

  const findObjectIdFromEventTarget = (target) => {
    let currentTarget = target

    while (currentTarget) {
      if (typeof currentTarget.id === "function") {
        const id = currentTarget.id()
        if (id) return id
      }

      if (typeof currentTarget.getParent !== "function") break
      currentTarget = currentTarget.getParent()
    }

    return null
  }

  const updateBoardTools = (patch) => {
    const timestamp = currentTimestamp()
    shouldSyncRef.current = true
    setBoardState((current) => ({
      ...current,
      tools: {
        ...current.tools,
        ...patch,
        bingo_grid: patch.bingo_grid
          ? normalizeBingoGrid({ ...patch.bingo_grid, updated_at: timestamp })
          : normalizeBingoGrid(current.tools?.bingo_grid),
        card_deck: patch.card_deck
          ? normalizeCardDeck({ ...patch.card_deck, updated_at: timestamp })
          : normalizeCardDeck(current.tools?.card_deck)
      },
      tool_updated_ats: {
        ...current.tool_updated_ats,
        ...Object.fromEntries(Object.keys(patch).map((toolName) => [toolName, timestamp]))
      },
      updated_at: timestamp
    }))
  }

  const updateStudentMode = (nextMode) => {
    const normalizedMode = normalizeStudentMode(nextMode)
    const timestamp = currentTimestamp()
    shouldSyncRef.current = true
    setBoardState((current) => ({
      ...current,
      student_mode: normalizedMode,
      student_mode_updated_at: timestamp,
      updated_at: timestamp
    }))
  }

  const setLocalMode = (nextMode) => {
    setMode(nextMode)

    if (role === "student") {
      updateStudentMode(nextMode)
    }
  }

  const toggleBingoGrid = () => {
    updateBoardTools({
      bingo_grid: {
        ...bingoGrid,
        enabled: !bingoGridEnabled
      }
    })
  }

  const resizeBingoGrid = (dimension, rawValue) => {
    const timestamp = currentTimestamp()
    const nextSize = Math.max(1, Math.min(Number(rawValue) || 1, 6))
    const rows = dimension === "rows" ? nextSize : bingoGrid.rows
    const columns = dimension === "columns" ? nextSize : bingoGrid.columns
    const nextCells = Array.from({ length: rows * columns }, (_, index) => bingoGrid.cells[index] || "")
    const nextCellUpdatedAts = Array.from({ length: rows * columns }, (_, index) => bingoGrid.cell_updated_ats[index] || timestamp)

    updateBoardTools({
      bingo_grid: {
        ...bingoGrid,
        rows,
        columns,
        cells: nextCells,
        cell_updated_ats: nextCellUpdatedAts
      }
    })
  }

  const updateBingoGridCell = (index, value) => {
    const timestamp = currentTimestamp()
    const nextCells = [...bingoGrid.cells]
    const nextCellUpdatedAts = [...bingoGrid.cell_updated_ats]
    nextCells[index] = value
    nextCellUpdatedAts[index] = timestamp

    updateBoardTools({
      bingo_grid: {
        ...bingoGrid,
        cells: nextCells,
        cell_updated_ats: nextCellUpdatedAts
      }
    })
  }

  const toggleCardDeck = () => {
    updateBoardTools({
      card_deck: {
        ...cardDeck,
        enabled: !cardDeckEnabled,
        draw_index: 0
      }
    })
  }

  const setCardDeckMode = (mode) => {
    const timestamp = currentTimestamp()
    const resetCards = cardDeck.cards.map((card) => ({ ...card, faceUp: false, x: undefined, y: undefined, updated_at: timestamp }))

    updateBoardTools({
      card_deck: {
        ...cardDeck,
        mode,
        draw_index: 0,
        cards: mode === "grid" ? layoutCardDeckCards(resetCards) : layoutDrawnPileCards(resetCards, cardPileLayout, 0)
      }
    })
  }

  const updateCardDeckFromText = (rawValue) => {
    const timestamp = currentTimestamp()
    setCardDeckDraft(rawValue)
    const parsedCards = rawValue
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((label, index) => ({
        id: `card-${index + 1}`,
        label,
        faceUp: false,
        updated_at: timestamp
      }))
    const cards = cardDeckIsPile ? layoutDrawnPileCards(parsedCards, cardPileLayout, 0) : layoutCardDeckCards(parsedCards)

    updateBoardTools({
      card_deck: {
        ...cardDeck,
        draw_index: 0,
        cards
      }
    })
  }

  const shuffleCardDeck = () => {
    const timestamp = currentTimestamp()
    const shuffledCards = [...cardDeck.cards]
      .sort(() => Math.random() - 0.5)
      .map((card) => ({ ...card, faceUp: false, x: undefined, y: undefined, updated_at: timestamp }))

    setCardShuffleFeedback(true)
    if (cardShuffleFeedbackTimerRef.current) clearTimeout(cardShuffleFeedbackTimerRef.current)
    cardShuffleFeedbackTimerRef.current = setTimeout(() => setCardShuffleFeedback(false), 1200)

    updateBoardTools({
      card_deck: {
        ...cardDeck,
        draw_index: 0,
        cards: cardDeckIsPile ? layoutDrawnPileCards(shuffledCards, cardPileLayout, 0) : layoutCardDeckCards(shuffledCards)
      }
    })
  }

  const drawTopCard = () => {
    if (!cardDeckIsPile) return
    if (cardDeck.draw_index >= cardDeck.cards.length) return

    const timestamp = currentTimestamp()
    const nextDrawIndex = cardDeck.draw_index + 1
    const nextCards = layoutDrawnPileCards(
      cardDeck.cards.map((card, index) => (
        index === cardDeck.draw_index ? { ...card, faceUp: true, updated_at: timestamp } : (
          index < nextDrawIndex ? card : { ...card, faceUp: false, x: undefined, y: undefined, updated_at: card.updated_at }
        )
      )),
      cardPileLayout,
      nextDrawIndex
    )

    updateBoardTools({
      card_deck: {
        ...cardDeck,
        draw_index: nextDrawIndex,
        cards: nextCards
      }
    })
  }

  const beginPileDrag = (clientX, clientY) => {
    if (!cardDeckIsPile) return
    if (cardDeck.draw_index >= cardDeck.cards.length) return null

    const timestamp = currentTimestamp()
    const nextDrawIndex = cardDeck.draw_index + 1
    const nextPoint = clientPointToBoardPoint(clientX, clientY)
    const dealtCardId = cardDeck.cards[cardDeck.draw_index].id
    const nextCards = layoutDrawnPileCards(
      cardDeck.cards.map((card, index) => (
        index === cardDeck.draw_index
          ? { ...card, faceUp: false, x: nextPoint.x, y: nextPoint.y, updated_at: timestamp }
          : (
            index < nextDrawIndex ? card : { ...card, faceUp: false, x: undefined, y: undefined, updated_at: card.updated_at }
          )
      )),
      cardPileLayout,
      nextDrawIndex
    )

    updateBoardTools({
      card_deck: {
        ...cardDeck,
        draw_index: nextDrawIndex,
        cards: nextCards
      }
    })

    return dealtCardId
  }

  const movePileDealtCard = (cardId, clientX, clientY) => {
    const nextPoint = clientPointToBoardPoint(clientX, clientY)
    const timestamp = currentTimestamp()

    shouldSyncRef.current = true
    setBoardState((current) => {
      const currentDeck = normalizeCardDeck(current.tools?.card_deck)

      return {
        ...current,
        updated_at: timestamp,
        tools: {
          ...current.tools,
          card_deck: {
            ...currentDeck,
            updated_at: timestamp,
            cards: currentDeck.cards.map((existingCard) => (
              existingCard.id === cardId
                ? { ...existingCard, x: nextPoint.x, y: nextPoint.y, updated_at: timestamp }
                : existingCard
            ))
          }
        },
        tool_updated_ats: {
          ...current.tool_updated_ats,
          card_deck: timestamp
        }
      }
    })
  }

  const handlePilePointerDown = (event) => {
    if (!cardDeckIsPile || remainingPileCount === 0) return

    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)

    pilePointerRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      dealtCardId: null,
      moved: false
    }
  }

  const handlePilePointerMove = (event) => {
    const activePointer = pilePointerRef.current
    if (!activePointer || activePointer.pointerId !== event.pointerId) return

    const moved = Math.abs(event.clientX - activePointer.startClientX) > 3 || Math.abs(event.clientY - activePointer.startClientY) > 3
    if (!moved && !activePointer.moved) return

    let nextPointer = activePointer

    if (!activePointer.moved) {
      const dealtCardId = beginPileDrag(event.clientX, event.clientY)
      if (!dealtCardId) return
      nextPointer = { ...activePointer, dealtCardId, moved: true }
      pilePointerRef.current = nextPointer
    }

    if (nextPointer.dealtCardId) {
      movePileDealtCard(nextPointer.dealtCardId, event.clientX, event.clientY)
    }
  }

  const handlePilePointerUp = (event) => {
    const activePointer = pilePointerRef.current
    if (!activePointer || activePointer.pointerId !== event.pointerId) return

    event.preventDefault()
    event.stopPropagation()
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    pilePointerRef.current = null

    if (!activePointer.moved) {
      drawTopCard()
    }
  }

  const handlePilePointerCancel = (event) => {
    const activePointer = pilePointerRef.current
    if (!activePointer || activePointer.pointerId !== event.pointerId) return

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    pilePointerRef.current = null
  }

  const toggleCardFace = (cardId) => {
    const timestamp = currentTimestamp()
    const nextCards = cardDeck.cards.map((card) => (
      card.id === cardId ? { ...card, faceUp: !card.faceUp, updated_at: timestamp } : card
    ))

    updateBoardTools({
      card_deck: {
        ...cardDeck,
        cards: nextCards
      }
    })
  }

  const handleCardPointerDown = (card, event) => {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)

    cardPointerRef.current = {
      cardId: card.id,
      pointerId: event.pointerId,
      moved: false,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: card.x,
      startY: card.y
    }
  }

  const handleCardPointerMove = (event) => {
    const activePointer = cardPointerRef.current
    if (!activePointer || activePointer.pointerId !== event.pointerId) return

    const deltaX = (event.clientX - activePointer.startClientX) / scale
    const deltaY = (event.clientY - activePointer.startClientY) / scale
    const moved = Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3
    const timestamp = currentTimestamp()

    if (moved && !activePointer.moved) {
      cardPointerRef.current = { ...activePointer, moved: true }
    }

    if (!moved && !activePointer.moved) return

    shouldSyncRef.current = true
    setBoardState((current) => {
      const currentDeck = normalizeCardDeck(current.tools?.card_deck)

      return {
        ...current,
        updated_at: timestamp,
        tools: {
          ...current.tools,
          card_deck: {
            ...currentDeck,
            updated_at: timestamp,
            cards: currentDeck.cards.map((existingCard) => (
              existingCard.id === activePointer.cardId
                ? { ...existingCard, x: activePointer.startX + deltaX, y: activePointer.startY + deltaY, updated_at: timestamp }
                : existingCard
            ))
          }
        },
        tool_updated_ats: {
          ...current.tool_updated_ats,
          card_deck: timestamp
        }
      }
    })
  }

  const handleCardPointerUp = (event) => {
    const activePointer = cardPointerRef.current
    if (!activePointer || activePointer.pointerId !== event.pointerId) return

    event.preventDefault()
    event.stopPropagation()
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    cardPointerRef.current = null
    if (!activePointer.moved) {
      toggleCardFace(activePointer.cardId)
    }
  }

  const handleCardPointerCancel = (event) => {
    const activePointer = cardPointerRef.current
    if (!activePointer || activePointer.pointerId !== event.pointerId) return

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    cardPointerRef.current = null
  }

  const spawnLetterTileFromTray = (letter, event) => {
    event.preventDefault()

    const tileId = crypto.randomUUID()
    const startPoint = clientPointToBoardPoint(event.clientX, event.clientY)
    const timestamp = currentTimestamp()

    shouldSyncRef.current = true
    setSelectedIds([tileId])
    setBoardState((current) => ({
      ...current,
      updated_at: timestamp,
      objects: [
        ...current.objects,
        {
          id: tileId,
          type: "tile",
          letter,
          x: startPoint.x,
          y: startPoint.y,
          updated_at: timestamp
        }
      ]
    }))
    trayDragRef.current = { tileId }
  }

  const openTextEditor = ({ id = null, text = "", x, y }) => {
    setGridCellDraft(null)
    setSelectedIds(id ? [id] : [])
    setTextDraft({
      kind: "text",
      id,
      value: text,
      x,
      y
    })
  }

  const openBingoGridEditor = (index) => {
    if (!isTeacher) return

    const row = Math.floor(index / bingoGrid.columns)
    const column = index % bingoGrid.columns

    setSelectedIds([])
    setTextDraft(null)
    setGridCellDraft({
      kind: "grid_cell",
      index,
      value: bingoGrid.cells[index] || "",
      x: bingoGridLayout.x + column * (bingoGridLayout.cellWidth + bingoGridLayout.gap) + 8,
      y: bingoGridLayout.y + row * (bingoGridLayout.cellHeight + bingoGridLayout.gap) + 8,
      width: bingoGridLayout.cellWidth - 16
    })
  }

  const openNextBingoGridEditor = (index, nextCells, step) => {
    const count = nextCells.length
    const nextIndex = (index + step + count) % count
    const row = Math.floor(nextIndex / bingoGrid.columns)
    const column = nextIndex % bingoGrid.columns

    setGridCellDraft({
      kind: "grid_cell",
      index: nextIndex,
      value: nextCells[nextIndex] || "",
      x: bingoGridLayout.x + column * (bingoGridLayout.cellWidth + bingoGridLayout.gap) + 8,
      y: bingoGridLayout.y + row * (bingoGridLayout.cellHeight + bingoGridLayout.gap) + 8,
      width: bingoGridLayout.cellWidth - 16
    })
  }

  const commitTextDraft = () => {
    if (!textDraft) return

    const value = textDraft.value.trim()
    if (!value) {
      setTextDraft(null)
      return
    }

    shouldSyncRef.current = true
    if (textDraft.id) {
      updateObject(textDraft.id, { text: value })
      setTextDraft(null)
      return
    }

    const timestamp = currentTimestamp()
    setBoardState((current) => ({
      ...current,
      updated_at: timestamp,
      objects: [
        ...current.objects,
        {
          id: crypto.randomUUID(),
          type: "text",
          x: textDraft.x,
          y: textDraft.y,
          text: value,
          fontSize: 28,
          fill: DRAW_COLOR,
          updated_at: timestamp
        }
      ]
    }))
    setTextDraft(null)
  }

  const commitGridCellDraft = () => {
    if (!gridCellDraft) return

    updateBingoGridCell(gridCellDraft.index, gridCellDraft.value.trim())
    setGridCellDraft(null)
  }

  const beginObjectDrag = (objectId, event) => {
    event.cancelBubble = true
    const activeIds = selectedIds.includes(objectId) ? selectedIds : [objectId]
    setSelectedIds(activeIds)
    dragGroupRef.current = {
      ids: activeIds,
      startX: event.target.x() / scale,
      startY: event.target.y() / scale,
      basePositions: Object.fromEntries(
        boardState.objects
          .filter((object) => activeIds.includes(object.id))
          .map((object) => [object.id, { x: object.x || 0, y: object.y || 0 }])
      )
    }
  }

  const moveDraggedObjects = (objectId, event) => {
    event.cancelBubble = true
    const dragGroup = dragGroupRef.current
    if (!dragGroup) return

    const deltaX = event.target.x() / scale - dragGroup.startX
    const deltaY = event.target.y() / scale - dragGroup.startY
    const timestamp = currentTimestamp()
    shouldSyncRef.current = true

    setBoardState((current) => ({
      ...current,
      updated_at: timestamp,
      objects: current.objects.map((object) => {
        if (!dragGroup.ids.includes(object.id)) return object

        const basePosition = dragGroup.basePositions[object.id]
        return {
          ...object,
          x: basePosition.x + deltaX,
          y: basePosition.y + deltaY,
          updated_at: timestamp
        }
      })
    }))
  }

  const selectedIdsForBox = (box) => (
    boardState.objects
      .filter((object) => intersects(box, objectBounds(object)))
      .map((object) => object.id)
  )

  const finishObjectDrag = (objectId, event) => {
    event.cancelBubble = true
    dragGroupRef.current = null
  }

  const handlePointerDown = (event) => {
    const stage = event.target.getStage()
    const point = stage.getPointerPosition()
    if (!point) return
    const boardX = point.x / scale
    const boardY = point.y / scale
    const clickedGridCellIndex = findBingoCellIndexAtPoint(boardX, boardY)
    const clickedObjectId = findObjectIdFromEventTarget(event.target)
    const clickedObject = clickedObjectId ? boardState.objects.find((object) => object.id === clickedObjectId) : null
    const clickedOnBoard =
      event.target === stage || event.target.name() === BACKGROUND_NAME || clickedGridCellIndex !== null

    if (mode === "erase" && clickedObject?.type === "line") {
      deleteObjectIds([clickedObject.id])
      setTextDraft(null)
      setGridCellDraft(null)
      setSelectionBox(null)
      return
    }

    if (mode === "draw" && clickedOnBoard) {
      setTextDraft(null)
      setGridCellDraft(null)
      setSelectionBox(null)
      setSelectionPreviewIds([])
      const timestamp = currentTimestamp()
      const nextLine = {
        id: crypto.randomUUID(),
        type: "line",
        x: 0,
        y: 0,
        points: [point.x / scale, point.y / scale],
        stroke: DRAW_COLOR,
        updated_at: timestamp
      }

      currentLineRef.current = nextLine.id
      setSelectedIds([])
      shouldSyncRef.current = true
      setBoardState((current) => ({
        ...current,
        updated_at: timestamp,
        objects: [...current.objects, nextLine]
      }))
      return
    }

    if (mode === "type" && clickedGridCellIndex !== null && isTeacher) {
      openBingoGridEditor(clickedGridCellIndex)
      return
    }

    if (mode === "type" && clickedOnBoard) {
      setGridCellDraft(null)
      openTextEditor({
        x: boardX,
        y: boardY
      })
      return
    }

    if (mode === "drag" && clickedOnBoard) {
      setTextDraft(null)
      setGridCellDraft(null)
      setSelectedIds([])
      setSelectionPreviewIds([])
      setSelectionBox({
        startX: boardX,
        startY: boardY,
        endX: boardX,
        endY: boardY
      })
    }
  }

  const handlePointerMove = (event) => {
    const stage = event.target.getStage()
    const point = stage.getPointerPosition()
    if (!point) return

    if (mode === "draw" && currentLineRef.current) {
      const nextX = point.x / scale
      const nextY = point.y / scale
      const timestamp = currentTimestamp()

      shouldSyncRef.current = true
      setBoardState((current) => ({
        ...current,
        updated_at: timestamp,
        objects: current.objects.map((object) => {
          if (object.id !== currentLineRef.current) return object
          return { ...object, points: [...object.points, nextX, nextY], updated_at: timestamp }
        })
      }))
    }

    if (mode === "drag" && selectionBox) {
      setSelectionBox((current) => {
        if (!current) return current

        const nextBox = { ...current, endX: point.x / scale, endY: point.y / scale }
        const normalizedBox = normalizeSelectionBox(nextBox)
        const hasArea = normalizedBox.width > 3 && normalizedBox.height > 3
        setSelectionPreviewIds(hasArea ? selectedIdsForBox(normalizedBox) : [])
        return nextBox
      })
    }
  }

  const handlePointerUp = () => {
    currentLineRef.current = null
    dragGroupRef.current = null

    if (!selectionBox) return

    const normalizedBox = normalizeSelectionBox(selectionBox)
    const hasArea = normalizedBox.width > 3 && normalizedBox.height > 3

    if (!hasArea) {
      setSelectionBox(null)
      setSelectionPreviewIds([])
      return
    }

    const nextSelectedIds = selectedIdsForBox(normalizedBox)

    setSelectedIds(nextSelectedIds)
    setSelectionPreviewIds([])
    setSelectionBox(null)
  }

  const clearObjectsByType = (types) => {
    if (!isTeacher) return

    const timestamp = currentTimestamp()
    shouldSyncRef.current = true
    setBoardState((current) => ({
      ...current,
      deleted_objects: {
        ...current.deleted_objects,
        ...Object.fromEntries(
          current.objects
            .filter((object) => types.includes(object.type))
            .map((object) => [object.id, timestamp])
        )
      },
      objects: current.objects.filter((object) => !types.includes(object.type)),
      updated_at: timestamp
    }))
    setSelectedIds([])
    setSelectionPreviewIds([])
    setSelectionBox(null)
  }

  const deleteSelection = () => {
    if (!isTeacher || selectedIds.length === 0) return
    deleteObjectIds(selectedIds)
  }

  return (
    React.createElement("section", { className: `whiteboard-app whiteboard-app--${role}` }, [
      isTeacher &&
        React.createElement("div", { className: "teacher-tools", key: "teacher-tools" }, [
          React.createElement("div", { className: "tool-panel", key: "tools" }, [
            React.createElement(
              "button",
              {
                className: `mode-button ${letterTileMatEnabled ? "is-active" : ""}`,
                key: "letter-tool-toggle",
                onClick: () => updateBoardTools({ letter_tile_mat: !letterTileMatEnabled }),
                type: "button"
              },
              letterTileMatEnabled ? "Hide Letter Tiles" : "Show Letter Tiles"
            ),
            React.createElement(
              "div",
              { className: "tool-toggle-group", key: "bingo-tool-group" },
              [
                React.createElement(
                  "button",
                  {
                    className: `mode-button ${bingoGridEnabled ? "is-active" : ""}`,
                    key: "bingo-tool-toggle",
                    onClick: toggleBingoGrid,
                    type: "button"
                  },
                  bingoGridEnabled ? "Hide Bingo Grid" : "Show Bingo Grid"
                ),
                bingoGridEnabled &&
                  React.createElement(
                    "button",
                    {
                      className: `mode-button mode-button--small ${showBingoSetup ? "is-active" : ""}`,
                      key: "bingo-size-toggle",
                      onClick: () => setShowBingoSetup((current) => !current),
                      type: "button"
                    },
                    "Size"
                  ),
                bingoGridEnabled && showBingoSetup &&
                  React.createElement("div", { className: "tool-popover", key: "bingo-size-popover" }, [
                    React.createElement("label", { className: "bingo-grid-controls__field", key: "rows-field" }, [
                      React.createElement("span", { key: "rows-label" }, "Rows"),
                      React.createElement("input", {
                        className: "bingo-grid-controls__input",
                        key: "rows-input",
                        max: 6,
                        min: 1,
                        onChange: (event) => resizeBingoGrid("rows", event.target.value),
                        type: "number",
                        value: bingoGrid.rows
                      })
                    ]),
                    React.createElement("label", { className: "bingo-grid-controls__field", key: "columns-field" }, [
                      React.createElement("span", { key: "columns-label" }, "Columns"),
                      React.createElement("input", {
                        className: "bingo-grid-controls__input",
                        key: "columns-input",
                        max: 6,
                        min: 1,
                        onChange: (event) => resizeBingoGrid("columns", event.target.value),
                        type: "number",
                        value: bingoGrid.columns
                      })
                    ])
                  ])
              ]
            ),
            React.createElement(
              "button",
              {
                className: `mode-button ${cardDeckEnabled ? "is-active" : ""}`,
                key: "card-tool-toggle",
                onClick: toggleCardDeck,
                type: "button"
              },
              cardDeckEnabled ? "Hide Card Deck" : "Show Card Deck"
            ),
            React.createElement("div", { className: "tool-toggle-group", key: "student-controls-group" }, [
              React.createElement(
                "button",
                {
                  className: `mode-button mode-button--small ${showStudentControls ? "is-active" : ""}`,
                  key: "student-controls-toggle",
                  onClick: () => setShowStudentControls((current) => !current),
                  type: "button"
                },
                "Student"
              ),
              showStudentControls &&
                React.createElement("div", { className: "tool-popover", key: "student-mode-panel" }, [
                  ...STUDENT_MODES.map((nextMode) =>
                    React.createElement(
                      "button",
                      {
                        className: `mode-button ${normalizeStudentMode(boardState.student_mode) === nextMode ? "is-active" : ""}`,
                        key: `student-mode-${nextMode}`,
                        onClick: () => updateStudentMode(nextMode),
                        type: "button"
                      },
                      MODE_LABELS[nextMode]
                    )
                  )
                ])
            ])
          ]),
          cardDeckEnabled &&
            React.createElement("div", { className: "tool-panel tool-panel--stacked", key: "card-config" }, [
              React.createElement("div", { className: "tool-panel__header", key: "header" }, [
                React.createElement("span", { className: "tool-panel__label", key: "card-config-label" }, "Cards"),
                React.createElement(
                  "button",
                  {
                    className: "mode-button mode-button--small",
                    key: "card-setup-toggle",
                    onClick: () => setShowCardSetup((current) => !current),
                    type: "button"
                  },
                  showCardSetup ? "Hide Editor" : "Show Editor"
                )
              ]),
              React.createElement("div", { className: "card-deck-controls", key: "card-layout-controls" }, [
                React.createElement(
                  "button",
                  {
                    className: `mode-button ${!cardDeckIsPile ? "is-active" : ""}`,
                    key: "card-grid-mode",
                    onClick: () => setCardDeckMode("grid"),
                    type: "button"
                  },
                  "Grid"
                ),
                React.createElement(
                  "button",
                  {
                    className: `mode-button ${cardDeckIsPile ? "is-active" : ""}`,
                    key: "card-pile-mode",
                    onClick: () => setCardDeckMode("pile"),
                    type: "button"
                  },
                  "Pile"
                ),
                React.createElement(
                  "button",
                  {
                    className: "mode-button",
                    key: "card-shuffle",
                    onClick: shuffleCardDeck,
                    type: "button"
                  },
                  cardShuffleFeedback ? "Shuffled" : "Shuffle"
                )
              ]),
              showCardSetup &&
                React.createElement("textarea", {
                  className: "card-deck-editor",
                  key: "card-editor",
                  onChange: (event) => updateCardDeckFromText(event.target.value),
                  placeholder: "Enter one card label per line",
                  rows: 8,
                  value: cardDeckDraft
                })
            ])
        ]),
      React.createElement("div", { className: "whiteboard-toolbar", key: "toolbar" }, [
        (role === "student" ? STUDENT_PRIMARY_MODES : ["draw", "type", "drag"]).map((nextMode) =>
          React.createElement(
            "button",
          {
              className: `mode-button ${mode === nextMode ? "is-active" : ""} ${role === "student" ? "mode-button--icon" : ""}`,
              key: nextMode,
              onClick: () => setLocalMode(nextMode),
              title: role === "student" ? MODE_LABELS[nextMode] : undefined,
              type: "button"
            },
            role === "student" ? STUDENT_MODE_ICONS[nextMode] : MODE_LABELS[nextMode]
          )
        ),
        React.createElement("div", { className: "whiteboard-toolbar__spacer", key: "spacer" }),
        role === "student" &&
          React.createElement(
            "button",
            {
              className: `mode-button mode-button--icon mode-button--eraser ${mode === "erase" ? "is-active" : ""}`,
              key: "student-erase",
              onClick: () => setLocalMode("erase"),
              title: MODE_LABELS.erase,
              type: "button"
            },
            React.createElement(StudentEraserIcon)
          ),
        isTeacher &&
          React.createElement(
            "button",
          {
            className: `mode-button ${mode === "erase" ? "is-active" : ""}`,
              key: "erase-toggle",
              onClick: () => setMode((current) => (current === "erase" ? "draw" : "erase")),
              type: "button"
            },
            "Eraser"
          ),
        isTeacher &&
          React.createElement(
            "button",
            { className: "mode-button", disabled: selectedIds.length === 0, key: "delete", onClick: deleteSelection, type: "button" },
            "Delete Selected"
          ),
        isTeacher &&
          React.createElement("div", { className: "tool-toggle-group", key: "clear-tools-group" }, [
            React.createElement(
              "button",
              {
                className: `mode-button mode-button--small clear-button ${showClearMenu ? "is-active" : ""}`,
                key: "clear-menu-toggle",
                onClick: () => setShowClearMenu((current) => !current),
                type: "button"
              },
              "Clear"
            ),
            showClearMenu &&
              React.createElement("div", { className: "tool-popover", key: "clear-menu" }, [
                React.createElement(
                  "button",
                  { className: "mode-button clear-button", key: "clear-drawings", onClick: () => clearObjectsByType(["line"]), type: "button" },
                  "Drawings"
                ),
                React.createElement(
                  "button",
                  { className: "mode-button clear-button", key: "clear-text", onClick: () => clearObjectsByType(["text"]), type: "button" },
                  "Text"
                ),
                React.createElement(
                  "button",
                  { className: "mode-button clear-button", key: "clear-tiles", onClick: () => clearObjectsByType(["tile"]), type: "button" },
                  "Tiles"
                ),
                React.createElement(
                  "button",
                  { className: "mode-button clear-button", key: "clear-board", onClick: () => clearObjectsByType(["line", "text", "tile"]), type: "button" },
                  "Board"
                )
              ])
          ])
      ]),
      React.createElement("div", { className: "whiteboard-stage-wrap", key: "stage", ref: stageContainerRef }, [
        gridCellDraft &&
          React.createElement("div", {
            className: "inline-text-editor inline-text-editor--grid-cell",
            key: "grid-cell-editor",
            style: {
              left: `${gridCellDraft.x * scale}px`,
              top: `${gridCellDraft.y * scale}px`,
              width: `${gridCellDraft.width * scale}px`
            }
          }, [
            React.createElement("input", {
              className: "inline-text-editor__input",
              key: "input",
              onChange: (event) => {
                const { value } = event.target
                setGridCellDraft((current) => (current ? { ...current, value } : current))
              },
              onKeyDown: (event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  commitGridCellDraft()
                }

                if (event.key === "Tab") {
                  event.preventDefault()
                  const nextCells = [...bingoGrid.cells]
                  nextCells[gridCellDraft.index] = gridCellDraft.value.trim()
                  updateBingoGridCell(gridCellDraft.index, nextCells[gridCellDraft.index])
                  openNextBingoGridEditor(gridCellDraft.index, nextCells, event.shiftKey ? -1 : 1)
                }

                if (event.key === "Escape") {
                  event.preventDefault()
                  setGridCellDraft(null)
                }
              },
              placeholder: "Cell text",
              ref: textInputRef,
              type: "text",
              value: gridCellDraft.value
            })
          ]),
        bingoGridEnabled &&
          React.createElement("div", {
            className: "bingo-grid-overlay",
            key: "bingo-grid-overlay",
            style: {
              left: `${bingoGridLayout.x * scale}px`,
              top: `${bingoGridLayout.y * scale}px`,
              width: `${bingoGridLayout.width * scale}px`
            }
          },
          React.createElement("div", {
            className: "bingo-grid",
            key: "bingo-grid",
            style: {
              gap: `${bingoGridLayout.gap * scale}px`,
              gridTemplateColumns: `repeat(${bingoGrid.columns}, minmax(0, 1fr))`
            }
          },
          bingoGrid.cells.map((cell, index) =>
            React.createElement("div", {
              className: "bingo-grid__cell",
              key: `bingo-cell-${index}`,
              style: {
                minHeight: `${bingoGridLayout.cellHeight * scale}px`
              }
            }, cell || "\u00a0")
          ))),
        cardDeckEnabled && cardDeck.cards.length > 0 && !cardDeckIsPile &&
          React.createElement("div", {
            className: "card-deck-overlay",
            key: "card-deck-overlay",
            style: { width: `${BOARD_WIDTH * scale}px`, height: `${BOARD_HEIGHT * scale}px` }
          },
          cardDeck.cards.map((card) =>
            React.createElement("button", {
              className: `card-deck-card ${card.faceUp ? "is-face-up" : ""}`,
              key: card.id,
              onPointerCancel: handleCardPointerCancel,
              onPointerDown: (event) => handleCardPointerDown(card, event),
              onPointerMove: handleCardPointerMove,
              onPointerUp: handleCardPointerUp,
              style: {
                left: `${card.x * scale}px`,
                top: `${card.y * scale}px`,
                width: `${cardDeckLayout.cardWidth * scale}px`,
                minHeight: `${cardDeckLayout.cardHeight * scale}px`
              },
              type: "button"
            }, [
              React.createElement("span", { className: "card-deck-card__face", key: "front" }, card.faceUp ? card.label : "?")
            ])
          )),
        cardDeckEnabled && cardDeck.cards.length > 0 && cardDeckIsPile &&
          React.createElement("div", {
            className: "card-pile-overlay",
            key: "card-pile-overlay",
            style: { width: `${BOARD_WIDTH * scale}px`, height: `${BOARD_HEIGHT * scale}px` }
          }, [
            React.createElement("div", {
              className: "card-pile-slot",
              key: "card-pile-stack",
              style: {
                left: `${cardPileLayout.pileX * scale}px`,
                top: `${cardPileLayout.pileY * scale}px`
              }
            }, [
              React.createElement("button", {
                className: "card-pile-draw-hitbox",
                disabled: remainingPileCount === 0,
                key: "card-pile-draw-hitbox",
                onPointerCancel: handlePilePointerCancel,
                onPointerDown: handlePilePointerDown,
                onPointerMove: handlePilePointerMove,
                onPointerUp: handlePilePointerUp,
                style: {
                  width: `${cardPileLayout.cardWidth * scale + 12}px`,
                  height: `${cardPileLayout.cardHeight * scale + 12}px`
                },
                type: "button"
              }),
              ...Array.from({ length: Math.min(remainingPileCount, 3) }, (_, index) =>
                React.createElement("div", {
                  className: "card-deck-card card-deck-card--stack",
                  key: `pile-card-${index}`,
                  style: {
                    left: `${index * 6}px`,
                    top: `${index * 6}px`,
                    width: `${cardPileLayout.cardWidth * scale}px`,
                    minHeight: `${cardPileLayout.cardHeight * scale}px`
                  }
                }, React.createElement("span", { className: "card-deck-card__face", key: "back" }, "?"))
              ),
              React.createElement("div", { className: "card-pile-label", key: "pile-label" }, `${remainingPileCount} left`)
            ]),
            ...drawnPileCards.map((card) =>
              React.createElement("button", {
                className: `card-deck-card ${card.faceUp ? "is-face-up" : ""}`,
                key: card.id,
                onPointerCancel: handleCardPointerCancel,
                onPointerDown: (event) => handleCardPointerDown(card, event),
                onPointerMove: handleCardPointerMove,
                onPointerUp: handleCardPointerUp,
                style: {
                  left: `${card.x * scale}px`,
                  top: `${card.y * scale}px`,
                  width: `${cardPileLayout.cardWidth * scale}px`,
                  minHeight: `${cardPileLayout.cardHeight * scale}px`
                },
                type: "button"
              }, [
                React.createElement("span", { className: "card-deck-card__face", key: "front" }, card.faceUp ? card.label : "?")
              ])
            )
          ]),
        textDraft &&
          React.createElement("div", {
            className: "inline-text-editor",
            key: "editor",
            style: {
              left: `${textDraft.x * scale}px`,
              top: `${textDraft.y * scale}px`
            }
          }, [
            React.createElement("input", {
              className: "inline-text-editor__input",
              key: "input",
              onChange: (event) => {
                const { value } = event.target
                setTextDraft((current) => (current ? { ...current, value } : current))
              },
              onKeyDown: (event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  commitTextDraft()
                }

                if (event.key === "Escape") {
                  event.preventDefault()
                  setTextDraft(null)
                }
              },
              placeholder: "Type here",
              ref: textInputRef,
              type: "text",
              value: textDraft.value
            }),
            React.createElement("div", { className: "inline-text-editor__actions", key: "actions" }, [
              React.createElement(
                "button",
                {
                  className: "mode-button",
                  key: "place",
                  onClick: commitTextDraft,
                  type: "button"
                },
                "Place"
              ),
              React.createElement(
                "button",
                {
                  className: "mode-button",
                  key: "cancel",
                  onClick: () => setTextDraft(null),
                  type: "button"
                },
                "Cancel"
              )
            ])
          ]),
        React.createElement(
          Stage,
          {
            className: "whiteboard-stage",
            height: BOARD_HEIGHT * scale,
            onMouseDown: handlePointerDown,
            onMouseMove: handlePointerMove,
            onMouseUp: handlePointerUp,
            onTouchEnd: handlePointerUp,
            onTouchMove: handlePointerMove,
            onTouchStart: handlePointerDown,
            width: canvasWidth
          },
          React.createElement(Layer, null, [
            React.createElement(Rect, {
              cornerRadius: 28,
              fill: "rgba(255, 253, 248, 0.03)",
              height: BOARD_HEIGHT * scale,
              key: "background",
              name: BACKGROUND_NAME,
              stroke: "#d7c9b4",
              strokeWidth: 2,
              width: canvasWidth
            }),
            selectionBox &&
              (() => {
                const normalizedBox = normalizeSelectionBox(selectionBox)
                return React.createElement(Rect, {
                  dash: [10, 6],
                  fill: "rgba(216, 102, 44, 0.12)",
                  height: normalizedBox.height * scale,
                  key: "selection-box",
                  listening: false,
                  stroke: "#d8662c",
                  strokeWidth: 2,
                  width: normalizedBox.width * scale,
                  x: normalizedBox.x * scale,
                  y: normalizedBox.y * scale
                })
              })(),
            ...renderedBoardObjects.map((object) => {
              const isSelected = activeSelectedIds.includes(object.id)

              if (object.type === "line") {
                return React.createElement(
                  Group,
                  {
                    draggable: mode === "drag",
                    id: object.id,
                    key: object.id,
                    listening: mode !== "draw",
                    onClick: () => setSelectedIds([object.id]),
                    onDragMove: (event) => moveDraggedObjects(object.id, event),
                    onDragStart: (event) => beginObjectDrag(object.id, event),
                    onDragEnd: (event) => finishObjectDrag(object.id, event),
                    x: (object.x || 0) * scale,
                    y: (object.y || 0) * scale
                  },
                  React.createElement(Line, {
                    hitStrokeWidth: 20,
                    lineCap: "round",
                    lineJoin: "round",
                    points: object.points.map((value) => value * scale),
                    stroke: isSelected ? "#d8662c" : object.stroke,
                    strokeWidth: 5
                  })
                )
              }

              if (object.type === "tile") {
                return React.createElement(
                  Group,
                  {
                    draggable: true,
                    id: object.id,
                    key: object.id,
                    onClick: () => setSelectedIds([object.id]),
                    onDragMove: (event) => moveDraggedObjects(object.id, event),
                    onDragStart: (event) => beginObjectDrag(object.id, event),
                    onDragEnd: (event) => finishObjectDrag(object.id, event),
                    x: object.x * scale,
                    y: object.y * scale
                  },
                  [
                    React.createElement(Rect, {
                      cornerRadius: 14,
                      fill: isSelected ? "#f5e27a" : (object.letter === BLANK_TILE ? "#efe8d6" : (VOWELS.has(object.letter) ? "#f5c4c4" : "#c9d7f6")),
                      height: TILE_SIZE * scale,
                      key: `${object.id}-rect`,
                      stroke: isSelected ? "#9a7b11" : "#9e927f",
                      strokeWidth: 2,
                      width: TILE_SIZE * scale
                    }),
                    React.createElement(Text, {
                      align: "center",
                      fill: "#243223",
                      fontFamily: "Avenir Next, Trebuchet MS, Verdana, sans-serif",
                      fontSize: 28 * scale,
                      fontStyle: "bold",
                      height: TILE_SIZE * scale,
                      key: `${object.id}-text`,
                      text: object.letter,
                      verticalAlign: "middle",
                      width: TILE_SIZE * scale
                    })
                  ]
                )
              }

              return React.createElement(Text, {
                draggable: mode === "drag",
                fill: isSelected ? "#d8662c" : object.fill,
                fontFamily: "Georgia",
                fontSize: object.fontSize * scale,
                id: object.id,
                key: object.id,
                listening: mode !== "draw",
                onClick: () => {
                  if (mode === "type") {
                    openTextEditor({
                      id: object.id,
                      text: object.text,
                      x: object.x,
                      y: object.y
                    })
                    return
                  }

                  setSelectedIds([object.id])
                },
                onDragMove: (event) => moveDraggedObjects(object.id, event),
                onDragStart: (event) => beginObjectDrag(object.id, event),
                onDragEnd: (event) => finishObjectDrag(object.id, event),
                text: object.text,
                x: object.x * scale,
                y: object.y * scale
              })
            })
          ]),
        ),
        letterTileMatEnabled &&
          React.createElement("div", { className: "letter-tray letter-tray--docked", key: "letter-tray" }, [
            React.createElement("div", { className: "letter-tray__grid", key: "tray-grid" },
              TRAY_TILES.map((letter) =>
                React.createElement(
                  "button",
                  {
                    className: `letter-tray__tile ${letter === BLANK_TILE ? "is-blank" : (VOWELS.has(letter) ? "is-vowel" : "is-consonant")}`,
                    key: letter || "blank",
                    onPointerDown: (event) => spawnLetterTileFromTray(letter, event),
                    type: "button"
                  },
                  letter
                )
              )
            )
          ])
        ]),
    ])
  )
}

export function mountLiveSession() {
  const mountPoint = document.getElementById("live-session-root")
  if (!mountPoint) return
  if (mountPoint.dataset.mounted === "true") return

  mountPoint.dataset.mounted = "true"
  const root = createRoot(mountPoint)
  const initialState = parseBoardState(mountPoint.dataset.sessionState)

  root.render(
    React.createElement(LiveSessionApp, {
      initialState,
      role: mountPoint.dataset.role,
      sessionId: mountPoint.dataset.sessionId,
      updateUrl: mountPoint.dataset.sessionUpdateUrl
    })
  )
}
